import { useEffect, useState, useMemo, useCallback } from "react";
import { useShallow } from "zustand/shallow";
import dayjs from "dayjs";
import classNames from "classnames";
import {
  Badge,
  ClickAwayListener,
  IconButton,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Tooltip,
} from "@mui/material";
import Icon from "@mdi/react";
import {
  mdiAccountMultipleOutline,
  mdiInformationOutline,
  mdiAccountMultiplePlusOutline,
  mdiClockOutline,
} from "@mdi/js";

import { GetAllGroups } from "api/group";
import { StorageBuckets } from "api/storage";
import AnimatedPlaceholder from "components/AnimatedPlaceholder";
import AppBar from "components/AppBar";
import AsyncAvatar from "components/AsyncAvatar";
import AuthApi from "api/auth";
import AvatarMenu from "containers/Groups/AvatarMenu";
import ChangeLog from "containers/ChangeLog";
import compareString from "utils/compareString";
import ContrastIcon from "components/ContrastIcon";
import FAB from "components/FAB";
import getNameInitials from "utils/getNameInitials";
import InstallApp from "components/InstallApp";
import IOSAddToHomeScreenTooltip from "components/IOSAddToHomeScreenTooltip";
import isActiveCountdown from "utils/isActiveCountdown";
import LocalStorage from "services/LocalStorage";
import lottiePlaceholders from "assets/placeholders";
import PageLayout from "components/PageLayout";
import ProfileApi from "api/profile";
import GroupSlugsApi from "api/group-slugs";
import PulseAnimation from "components/PulseAnimation";
import useBoundStore, { initialStore } from "store";
import useNotifier from "hooks/useNotifier";
import VirtualizedList from "components/VirtualizedList";
import { oneSignalCanRequestPermission } from "services/OneSignal";
import { appVersion } from "utils/env";
import NotificationDialog from "./NotificationDialog";
import styles from "./groups.module.scss";

export interface GroupsProps {
  onCreateGroupClick: () => void;
  onFriendsClick: () => void;
  onGroupClick: (group: GetAllGroups) => void;
  onSettingsClick: () => void;
}

function Groups(props: GroupsProps) {
  const { onCreateGroupClick, onFriendsClick, onGroupClick, onSettingsClick } = props;
  const [isAnnouncementOpen, setIsAnnouncementOpen] = useState(false);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const { auth, profile, setUser } = useBoundStore(
    useShallow((state) => ({
      auth: state.user.auth,
      profile: state.user.profile,
      setUser: state.user.set,
    }))
  );
  const generalSettings = useBoundStore((state) => state.generalSettings);
  const selected = useBoundStore((state) => state.selected);
  const { isMobileDevice, appStatus, smallAvatars } = useBoundStore(
    useShallow((state) => ({
      isMobileDevice: state.miscellaneous.isMobileDevice,
      appStatus: state.miscellaneous.appStatus,
      smallAvatars: state.miscellaneous.smallAvatars,
    }))
  );
  const { events, friendRequests, groups } = useBoundStore(
    useShallow((state) => ({
      events: state.appData.events,
      groups: state.appData.groups.sort((a, b) => compareString(a.name, b.name)),
      friendRequests: state.appData.friendRequests,
    }))
  );
  const localStorage = LocalStorage.getInstance();
  const { showNotification } = useNotifier();
  const authApi = useMemo(() => new AuthApi(), []);
  const groupSlugsApi = useMemo(() => new GroupSlugsApi(showNotification), [showNotification]);
  const profileApi = useMemo(() => new ProfileApi(showNotification), [showNotification]);
  const receivedFriendRequests = friendRequests.length;

  // FIXME: may not be the best place to put this
  /**
   * Get user profile
   */
  const getUserProfile = useCallback(async (): Promise<void> => {
    if (!profile && auth?.id) {
      const fetchedProfile = await profileApi.getUserProfile(auth.id);
      setUser({ profile: fetchedProfile });
    }
  }, [profileApi, setUser, auth, profile]);

  useEffect(() => {
    (async () => {
      if (selected.groupInviteLink) {
        const { groupName, error } = await groupSlugsApi.joinGroupViaLink(selected.groupInviteLink);
        selected.set({ groupInviteLink: null });

        if (!error) {
          showNotification({
            message: `Successfully joined '${groupName}' group`,
            severity: "success",
          });
        }
      }
    })();
  }, [selected.groupInviteLink, selected.set]);

  /**
   * Set user profile in global state
   */
  useEffect(() => {
    getUserProfile();
  }, [getUserProfile]);

  /**
   * Check to see if users has seen the change log and if he denied the install pwa banner
   */
  useEffect(() => {
    const changeLogVersion = localStorage.getChangelogVersion();
    const notificationDialogShown = localStorage.getNotificationDialogShown();

    // Render changelog
    if (changeLogVersion !== appVersion && appVersion === appStatus?.changelog.version) {
      setChangelogOpen(true);
    }

    // Render notification dialog
    if (!notificationDialogShown && oneSignalCanRequestPermission()) {
      setNotificationDialogOpen(true);
    }
  }, [appStatus?.changelog.version, localStorage]);

  /**
   * Handle group click
   *
   * @param group
   */
  function handleGroupClick(group: GetAllGroups): void {
    if (group.id !== selected.groupId) {
      selected.set({ groupId: group.id, eventId: null });
    }
    onGroupClick(group);
  }

  /**
   * Handle announcement click
   */
  function handleAnnouncementClick(): void {
    setIsAnnouncementOpen(true);
    if (appStatus?.announcement.date) {
      localStorage.setLastSeenAnnouncementDate(appStatus.announcement.date);
    }
  }

  /**
   * Handle logout click
   */
  async function handleLogoutClick(): Promise<void> {
    await authApi.signOut();
    useBoundStore.setState({ ...initialStore, generalSettings });
    localStorage.setAppData(null);
  }

  function handleCloseChangelog(): void {
    setChangelogOpen(false);
  }

  function handleCloseNotification(): void {
    setNotificationDialogOpen(false);
  }

  /**
   * Render friends icon
   *
   * @returns
   */
  function renderFriendsIcon(): JSX.Element {
    return (
      <IconButton aria-label='Friends' key='action-1' className={styles.friendsIcon} onClick={onFriendsClick}>
        <Badge
          badgeContent={receivedFriendRequests}
          color={generalSettings.theme === "light" ? "default" : "primary"}
          className={classNames({
            [styles.lightThemeBadge]: generalSettings.theme === "light",
          })}
        >
          <ContrastIcon path={mdiAccountMultipleOutline} size={1} />
        </Badge>
      </IconButton>
    );
  }

  /**
   * Render announcement icon
   *
   * @returns
   */
  function renderAnnouncementIcon(): JSX.Element {
    const lastSeenAnnouncementDate = localStorage.getLastSeenAnnouncementDate();
    let stopAnimation = false;
    if (lastSeenAnnouncementDate) {
      stopAnimation = dayjs.unix(lastSeenAnnouncementDate).isSame(dayjs.unix(appStatus!.announcement.date));
    }

    return (
      <ClickAwayListener onClickAway={() => setIsAnnouncementOpen(false)}>
        <Tooltip
          PopperProps={{
            disablePortal: true,
          }}
          onClose={() => setIsAnnouncementOpen(false)}
          open={isAnnouncementOpen}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          title={appStatus!.announcement.text}
        >
          <IconButton aria-label='Announcement' onClick={handleAnnouncementClick}>
            <PulseAnimation
              variant={generalSettings.theme === "dark" ? appStatus!.announcement.type : "default"}
              stop={stopAnimation}
            >
              <Icon path={mdiInformationOutline} size={1} />
            </PulseAnimation>
          </IconButton>
        </Tooltip>
      </ClickAwayListener>
    );
  }

  /**
   * Render app bar
   *
   * @returns
   */
  function renderAppBar(): JSX.Element {
    const actions = [
      renderFriendsIcon(),
      <AvatarMenu key='action-2' onLogoutClick={handleLogoutClick} onSettingsClick={onSettingsClick} />,
    ];

    if (appStatus?.announcement.active) {
      actions.unshift(renderAnnouncementIcon());
    }

    return <AppBar actions={actions} backAction={false} title='GroupUp' showLogo />;
  }

  /**
   * Generate items which will be used to display de virtualized list
   *
   * @returns
   */
  function generateVirtualizedList(): JSX.Element[] {
    const vrList: JSX.Element[] = [];
    vrList.push(<InstallApp />);

    groups.forEach((group) => {
      const groupEvents = events.filter((event) => event.group_id === group.id);
      const activeEvents = groupEvents.filter((event) => isActiveCountdown(event.vote_before)).length;

      vrList.push(
        <ListItemButton
          className={classNames({
            [styles.selectedGroup]: group.id === selected.groupId && !isMobileDevice,
          })}
          onClick={() => handleGroupClick(group)}
        >
          <ListItemAvatar>
            <AsyncAvatar
              avatarFile={group.avatar ? smallAvatars[group.avatar] : undefined}
              avatarName={group.avatar}
              bucket={StorageBuckets.GROUPS_AVATARS}
              placeholder={getNameInitials(group.name)}
            />
          </ListItemAvatar>
          <ListItemText
            primary={group.name}
            secondary={
              <>
                {activeEvents > 0 && (
                  <span className={styles.eventStatus}>
                    {activeEvents}
                    <Icon path={mdiClockOutline} size={0.65} className={styles.successClockIcon} title='Active' />
                  </span>
                )}
                {groupEvents.length - activeEvents > 0 && (
                  <span className={styles.eventStatus}>
                    {groupEvents.length - activeEvents}
                    <Icon path={mdiClockOutline} size={0.65} className={styles.warningClockIcon} title='Inactive' />
                  </span>
                )}
                {activeEvents < 1 && groupEvents.length - activeEvents < 1 && <span>No active events</span>}
              </>
            }
          />
        </ListItemButton>
      );
    });

    return vrList;
  }

  return (
    <PageLayout
      appBar={renderAppBar()}
      fab={
        <FAB aria-label='add-group' onClick={onCreateGroupClick} icon={mdiAccountMultiplePlusOutline} title='Plus' />
      }
      feedback
      paddingBottom={false}
    >
      {groups.length === 0 ? (
        <AnimatedPlaceholder
          animationData={lottiePlaceholders.group}
          text="Create a new group by pressing the '+' button"
        />
      ) : (
        <VirtualizedList
          estimateSize={72}
          rows={generateVirtualizedList()}
          style={{ paddingTop: isMobileDevice ? 0 : "16px" }}
        />
      )}

      <IOSAddToHomeScreenTooltip />
      <ChangeLog
        changes={appStatus?.changelog.changes}
        open={changelogOpen && !notificationDialogOpen}
        onClose={handleCloseChangelog}
      />
      <NotificationDialog open={notificationDialogOpen} onClose={handleCloseNotification} />
    </PageLayout>
  );
}

export default Groups;
